import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isNotNull<T>(value: T | null | undefined): value is T {
  return value != null;
}

/**
 * Money for display, or a dash when there is no figure to show.
 *
 * The dash is not decoration: `formatCurrency(null as never)` renders "$0.00",
 * so every caller holding a nullable amount has a one-character slip between it
 * and telling an operator a contract is settled when the backend said it could
 * not say. Routing those through here keeps "we do not know" and "zero" looking
 * different on screen, which is the whole point.
 */
export function formatCurrencyOrUnavailable(
  value: number | null | undefined,
  currency: string = 'USD',
  unavailable: string = '—'
): string {
  if (value == null) return unavailable;
  return formatCurrency(value, currency);
}

export function formatCurrency(
  value: number,
  currency: string = 'USD'
): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Fallback for unknown currency codes
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function formatUSD(value: number): string {
  return formatCurrency(value, 'USD');
}

/**
 * Shortens a long opaque identifier to `019f921d…81a9d`.
 *
 * Contract UUIDs are the only handle operators have — there is no
 * human-readable contract number in the schema — so the full value must stay
 * reachable (tooltip + copy) even though all 36 characters would swamp a row.
 */
export function truncateId(value: string, head = 8, tail = 5): string {
  // Nothing to gain from truncating a value that already fits.
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}
